import { planInput, planOf } from "../bot/plan";
import { AIR_TAKEOFF, airDelay, routesFor } from "../bot/routes";
import { buildRules } from "../content/rules";
import { sub } from "../shared/fixed";
import { FIGHTER } from "../sim";
import { setState } from "../sim/fighter";
import type { Scenario } from "./scenario";

/**
 * Сцена обмера касания (консультант 05.11.2026: «кость в кадр засчитанного попадания ≤ 20 px от тела соперника на
 * всех приёмах»): игрок 1 делает приём путём бота (bot/routes.ts — тот же, что проверяет тест), манекен стоит на краю
 * досягаемости — дальше всего, откуда удар ещё засчитывается. Хуже для касания не бывает. Имя сцены: reach-<id приёма>.
 */
const rules = buildRules(["mannequin", "mannequin"], "foundry");
const routes = routesFor(rules.kits[0]);
/** Полуширина тела соперника в мире (content/heroes: hurtStand x = −27) — зона удара засчитывается, коснувшись её. */
const BODY_HALF = 27;
/** Манекен у правой стены (арена до 896): отброс первых ударов серии его не уносит — следующий удар достаёт. */
const WALL_X = 880;
/** Трение мира на земле, пикселей за кадр за кадр (sim/fighter.ts FRICTION). */
const FRICTION_PX = 0.45;
/** Разбег с бега: двойное «вперёд» и ~10 кадров бега до удара. */
const RUN_UP = 100;

export function reachScenario(name: string): Scenario | undefined {
  if (!name.startsWith("reach-")) return undefined;
  const id = name.slice("reach-".length);
  const route = routes.get(id);
  const ability = rules.kits[0].find((candidate) => candidate.id === id);
  if (!route || !ability?.hitbox) return undefined;
  const front = ability.hitbox.x + ability.hitbox.w;
  // край досягаемости плюс сколько боец сам пройдёт до удара: ход корня клипа, подача, разбег
  // проход подскока вперёд до удара: в воздухе — без трения, на земле (подскок без высоты) — гаснет трением
  const leap = (ability.actions ?? [])
    .filter((cue) => cue.type === "leap")
    .reduce((sum, cue) => {
      const frames = Math.max(0, ability.startup - cue.frame);
      const speed = cue.params?.forward ?? 0;
      if ((cue.params?.up ?? 0) > 0) return sum + speed * frames;
      let path = 0;
      for (let step = 0; step < frames; step++) path += Math.max(0, speed - FRICTION_PX * step);
      return sum + path;
    }, 0);
  // ход корня клипа — прирост за кадр: путь до удара — сумма; подача — только у приёма без хода корня; прыжок вперёд
  // удара в воздухе — скорость прыжка героя за отрыв, задержку и разгон удара
  const motion = (ability.motion ?? []).slice(0, ability.startup).reduce((sum, step) => sum + step, 0);
  const advance = ability.motion ? 0 : (ability.advance ?? 0) * ability.startup;
  const hero = rules.heroes[0];
  const jump = ability.input.air ? hero.jumpForward * (AIR_TAKEOFF + airDelay(ability) + ability.startup) : 0;
  const travel = motion + advance + leap + jump;
  const edge = front + BODY_HALF - 2 + travel + (ability.input.run ? RUN_UP : 0);
  const gap = Math.min(edge, route.needs.near ?? edge);
  const plan = planOf(route);
  return {
    caption: `обмер касания: ${ability.name} — манекен на краю досягаемости (${String(Math.round(gap))} px)`,
    setup: (state) => {
      const [me, other] = state.fighters;
      me.x = sub(WALL_X - Math.max(76, gap));
      me.facing = 1;
      me.meter = Math.max(me.meter, route.needs.meter);
      other.x = sub(WALL_X);
      other.facing = -1;
      other.quiet = -1_000_000;
      me.quiet = -1_000_000;
      if (route.needs.lying) setState(me, FIGHTER.knockdown);
    },
    input: (frame, state) => [
      planInput(plan, state.fighters[0], state.fighters[1], rules.kits[0], state.frame + 1) ?? 0,
      0,
    ],
  };
}
